import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { RichText } from '../../components/RichText';
import { RichTextEditor } from '../../components/RichTextEditor';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import {
  useAnnouncements,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from './queries';
import { Select } from '../../components/Select';

const LEVELS = ['info', 'warning', 'outage'] as const;

const TONE: Record<string, 'info' | 'warning' | 'danger'> = {
  info: 'info',
  warning: 'warning',
  outage: 'danger',
};

/**
 * Ankuendigungen verwalten -- F3-10, K-56.
 *
 * **Das Banner ohne diese Maske war ein halbes Feature.** Es zeigte, was
 * niemand einstellen konnte -- eine Anzeige, die nie etwas anzeigt.
 *
 * **Die Liste zeigt auch Abgelaufenes.** Wer wissen will, ob die Stoerung von
 * gestern noch steht, sucht sie hier; eine Liste, die nur das gerade
 * Sichtbare enthaelt, beantwortet die Frage nicht, sondern verbirgt sie.
 *
 * **Ohne Ende laeuft eine Ankuendigung bis auf Widerruf.** Das ist der
 * haeufigste Fall bei einer Stoerung: Man weiss beim Schreiben nicht, wann
 * sie behoben ist. Beenden heisst dann: Ende auf jetzt setzen -- und dafuer
 * gibt es den Knopf, damit niemand ein Datum von Hand tippen muss.
 */
export function AnnouncementPanel() {
  const { t, i18n } = useTranslation();
  const announcements = useAnnouncements();
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const remove = useDeleteAnnouncement();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<(typeof LEVELS)[number]>('info');
  const [inPortal, setInPortal] = useState(true);
  const [inAgentUi, setInAgentUi] = useState(true);

  const now = Date.now();

  return (
    <div className="stack">
      <section className="card card--flush">
        {announcements.isPending ? <SkeletonTable columns={5} /> : null}
        {announcements.isError ? <ErrorNotice error={announcements.error} /> : null}

        {announcements.data?.length === 0 ? (
          <EmptyState
            icon="announcement"
            title={t('announcements.empty')}
            hint={t('announcements.emptyHint')}
          />
        ) : null}

        {announcements.data && announcements.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('announcements.headline')}</th>
                  <th scope="col">{t('announcements.level')}</th>
                  <th scope="col">{t('announcements.shownIn')}</th>
                  <th scope="col">{t('announcements.period')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {announcements.data.map((announcement) => {
                  const beendet =
                    announcement.ends_at !== null && new Date(announcement.ends_at).getTime() < now;
                  return (
                    <tr key={announcement.id}>
                      <td>
                        {announcement.title}
                        {announcement.body ? (
                          <RichText html={announcement.body} className="table__muted" />
                        ) : null}
                      </td>
                      <td>
                        <Badge tone={TONE[announcement.level] ?? 'info'}>
                          {t(`announcementLevel.${announcement.level}`)}
                        </Badge>
                      </td>
                      <td className="table__muted">
                        {[
                          announcement.show_in_agent_ui ? t('announcements.agents') : null,
                          announcement.show_in_portal ? t('announcements.portal') : null,
                        ]
                          .filter(Boolean)
                          .join(', ') || t('announcements.nowhere')}
                      </td>
                      <td className="table__muted">
                        {announcement.ends_at
                          ? formatDateTime(announcement.ends_at, i18n.language)
                          : t('announcements.openEnded')}
                        {beendet ? (
                          <>
                            {' '}
                            <Badge tone="neutral">{t('announcements.over')}</Badge>
                          </>
                        ) : null}
                      </td>
                      <td>
                        {!beendet ? (
                          <Button
                            variant="ghost"
                            busy={update.isPending}
                            onClick={() => {
                              // Beenden heisst: Ende auf jetzt. Das Loeschen
                              // nimmt der Nachwelt den Beleg, dass es die
                              // Stoerung gab.
                              update.mutate({
                                announcementId: announcement.id,
                                payload: { ends_at: new Date().toISOString() },
                              });
                            }}
                          >
                            {t('announcements.end')}
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(announcement.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {update.isError ? <ErrorNotice error={update.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <section className="card">
        <h2>{t('announcements.new')}</h2>
        <p className="table__muted">{t('announcements.hint')}</p>

        <div className="field">
          <label className="field__label" htmlFor="announcement-title">
            {t('announcements.headline')}
          </label>
          <input
            id="announcement-title"
            className="field__control"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="announcement-body">
            {t('announcements.body')}
          </label>
          {/* Ohne Ueberschriften, ohne Bild, ohne Tabelle: Eine
              Ankuendigung steht als Band ueber der Oberflaeche und im
              Portal -- dort ist Platz fuer zwei Zeilen, nicht fuer eine
              Seite. */}
          <RichTextEditor
            id="announcement-body"
            value={body}
            onChange={setBody}
            ariaLabel={t('announcements.body')}
            features={{ headings: false, image: false, table: false }}
            minRows={3}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="announcement-level">
            {t('announcements.level')}
          </label>
          <Select
            id="announcement-level"
            className="field__control"
            value={level}
            onChange={(event) => {
              setLevel(event.target.value as (typeof LEVELS)[number]);
            }}
          >
            {LEVELS.map((value) => (
              <option key={value} value={value}>
                {t(`announcementLevel.${value}`)}
              </option>
            ))}
          </Select>
          <span className="field__hint">{t('announcements.levelHint')}</span>
        </div>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={inAgentUi}
            onChange={(event) => {
              setInAgentUi(event.target.checked);
            }}
          />
          {t('announcements.showAgents')}
        </label>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={inPortal}
            onChange={(event) => {
              setInPortal(event.target.checked);
            }}
          />
          {t('announcements.showPortal')}
        </label>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!title.trim()}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              {
                title: title.trim(),
                body,
                level,
                show_in_portal: inPortal,
                show_in_agent_ui: inAgentUi,
              },
              {
                onSuccess: () => {
                  setTitle('');
                  setBody('');
                  setLevel('info');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>
    </div>
  );
}
